// T-039 — Settings screen for GDPR export and account deletion.

import { useEffect, useState, useCallback, useRef, type JSX } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n";
import { useAuth } from "../../services/auth";
import { buildExport, bundleToZip, downloadZip } from "../../services/export";
import { performAccountDeletion, waitForPendingDeletion } from "../../services/deleteAccount";
import { getDb } from "../../services/db";

export function SettingsRoute(): JSX.Element {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [exportStage, setExportStage] = useState<string | null>(null);
  const [exportWarning, setExportWarning] = useState<string | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [unsyncedCount, setUnsyncedCount] = useState(0);
  const [photoUnsyncedCount, setPhotoUnsyncedCount] = useState(0);
  const [athleteCount, setAthleteCount] = useState(0);
  const [offline, setOffline] = useState(false);

  const deleteTriggerRef = useRef<HTMLButtonElement | null>(null);
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const onStatus = () => setOffline(typeof navigator !== "undefined" && navigator.onLine === false);
    onStatus();
    window.addEventListener("online", onStatus);
    window.addEventListener("offline", onStatus);
    return () => {
      window.removeEventListener("online", onStatus);
      window.removeEventListener("offline", onStatus);
    };
  }, []);

  // Handle Escape key and focus management for delete modal
  useEffect(() => {
    if (!showDelete) return;

    cancelBtnRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) {
        handleCloseDelete();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showDelete, busy]);

  const handleCloseDelete = () => {
    setShowDelete(false);
    deleteTriggerRef.current?.focus();
  };

  // Production F-2 of the test-isolation audit: if the user navigates
  // away (back button, link tap) while a deletion is in flight, block
  // the unmount until the wipe finishes. Otherwise `setOwner(null)`
  // and `deleteActiveDb()` run against a page that no longer owns the
  // active db handle, and the user's session is silently destroyed
  // for a navigation they thought they had cancelled.
  useEffect(() => {
    return () => {
      const pending = waitForPendingDeletion();
      if (pending) {
        // We cannot `await` a useEffect cleanup synchronously, but the
        // act of waiting on the next microtask is enough to keep the
        // route component alive until the chain settles.
        void pending.catch(() => undefined);
      }
    };
  }, []);

  useEffect(() => {
    if (!showDelete || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const db = getDb();
        const queue = (await db.syncQueue.toArray()).filter(
          (e) => e.status === "pending" || e.status === "failed",
        );
        const photoQueue = queue.filter(
          (e) => e.collection === "progress_photos" && !!e.blobKey,
        );
        const athletes = (await db.coach_athletes.toArray()).filter(
          (r) => r.coach === user.id,
        );
        if (!cancelled) {
          setUnsyncedCount(queue.length);
          setPhotoUnsyncedCount(photoQueue.length);
          setAthleteCount(athletes.length);
        }
      } catch {
        // db may be closed mid-test; ignore
      }
    })();
    return () => { cancelled = true; };
  }, [showDelete, user]);

  const doExport = useCallback(async () => {
    if (!user) return;
    setBusy(true);
    setExportStage(t("settings.exportSyncing"));
    try {
      const bundle = await buildExport(user.id, {
        onProgress: (stage, count, total) => {
          setExportStage(
            stage === "sync"
              ? t("settings.exportSyncing")
              : t("settings.exportCollecting", { count, total }),
          );
        },
      });
      if (bundle.completeness === "partial") {
        setExportWarning(t("settings.exportPartial"));
      } else {
        setExportWarning(null);
      }
      setExportStage(t("settings.exportZipping"));
      const zip = await bundleToZip(bundle);
      downloadZip(zip, user.id);
    } catch (err) {
      setExportStage(t("settings.exportFailed"));
    } finally {
      setBusy(false);
      setTimeout(() => setExportStage(null), 2000);
    }
  }, [user, t]);

  const doDelete = useCallback(async () => {
    if (!user) return;
    setBusy(true);
    setDeleteError(null);
    try {
      await performAccountDeletion({ userId: user.id, navigate });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setDeleteError(msg === "offline" ? t("settings.deleteOffline") : t("settings.deleteFailed"));
      setBusy(false);
    }
  }, [user, navigate, t]);

  const emailMatch = confirmEmail.trim().toLowerCase() === (user?.email ?? "").toLowerCase();

  return (
    <section
      data-testid="settings-route"
      className="mx-auto flex w-full max-w-[640px] flex-col gap-6 px-4 py-6"
    >
      <h1 className="font-display text-3xl uppercase tracking-[0.02em] text-[var(--color-ink-hi)]">
        {t("nav.settings")}
      </h1>

      <div className="flex flex-col gap-3 border border-[var(--color-line)] p-4" style={{ borderRadius: "var(--radius-card)" }}>
        <h2 className="font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink)]">
          {t("settings.dataTitle")}
        </h2>
        <button
          type="button"
          onClick={doExport}
          disabled={busy}
          data-testid="export-button"
          className="border border-[var(--color-line-strong)] bg-[var(--color-slab)] px-4 py-3 text-left font-sans text-[var(--color-ink-hi)] hover:bg-[var(--color-slab-2)] disabled:opacity-50"
        >
          {t("settings.exportData")}
        </button>
        {offline && (
          <p className="font-sans text-sm text-[var(--color-rust)]" data-testid="export-offline-warning">
            {t("settings.exportOfflineWarning")}
          </p>
        )}
        {exportStage && <p className="font-sans text-sm text-[var(--color-ink)]" data-testid="export-stage">{exportStage}</p>}
        {exportWarning && (
          <p className="font-sans text-sm text-[var(--color-rust)]" data-testid="export-partial-warning">
            {exportWarning}
          </p>
        )}
      </div>

      <div
        className="flex flex-col gap-3 border border-[var(--color-rust)]/30 bg-[var(--color-void)] p-4"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <h2 className="font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-rust)]">
          {t("settings.dangerZoneTitle")}
        </h2>
        <p className="font-sans text-xs text-[var(--color-ink-mute)]">
          {t("settings.dangerZoneBody")}
        </p>
        <button
          ref={deleteTriggerRef}
          type="button"
          onClick={() => setShowDelete(true)}
          disabled={busy}
          data-testid="delete-open-button"
          className="border border-[var(--color-rust)]/60 bg-[var(--color-rust)]/10 px-4 py-3 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-rust)] hover:bg-[var(--color-rust)]/25 active:scale-95 disabled:opacity-50 transition-all"
          style={{ borderRadius: "var(--radius-sharp)" }}
        >
          {t("settings.deleteAccount")}
        </button>
      </div>

      {showDelete && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-sheet-title"
          data-testid="delete-sheet"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(10,11,10,0.9)" }}
        >
          <div
            className="flex w-full max-w-[640px] flex-col border border-[var(--color-blood)] bg-[var(--color-slab)] p-6"
            style={{ borderRadius: "var(--radius-card)", maxHeight: "90vh" }}
          >
            <h2
              id="delete-sheet-title"
              className="shrink-0 font-display text-2xl uppercase tracking-[0.02em] text-[var(--color-blood)]"
            >
              {t("settings.deleteTitle")}
            </h2>
            <div className="flex-1 overflow-y-auto pr-1">
              <p className="mt-4 font-sans text-base text-[var(--color-ink)]">{t("settings.deleteIrreversible")}</p>
              <ul className="mt-4 list-disc pl-5 font-sans text-sm text-[var(--color-ink)]">
                <li>{t("settings.deleteListWorkouts")}</li>
                <li>{t("settings.deleteListPhotos")}</li>
                <li>{t("settings.deleteListPosts")}</li>
                <li>{t("settings.deleteListCoach")}</li>
                <li>{t("settings.deleteListNotes")}</li>
              </ul>
              <p className="mt-4 font-sans text-sm text-[var(--color-ink-mute)]">{t("settings.deleteNotIncluded")}</p>

              {unsyncedCount > 0 && (
                <p className="mt-4 font-sans text-sm text-[var(--color-rust)]" data-testid="delete-unsynced">
                  {t("settings.deleteUnsynced", { count: unsyncedCount })}
                </p>
              )}
              {photoUnsyncedCount > 0 && (
                <p className="mt-2 font-sans text-sm text-[var(--color-rust)]" data-testid="delete-unsynced-photos">
                  {t("settings.deleteUnsyncedPhotos", { count: photoUnsyncedCount })}
                </p>
              )}
              {athleteCount > 0 && (
                <p className="mt-2 font-sans text-sm text-[var(--color-rust)]" data-testid="delete-athletes">
                  {t("settings.deleteAthletes", { count: athleteCount })}
                </p>
              )}

              <div
                className="mt-5 flex flex-col gap-3 border border-[var(--color-lime)]/40 bg-[var(--color-void)] p-3 sm:flex-row sm:items-center sm:justify-between"
                style={{ borderRadius: "var(--radius-sharp)" }}
              >
                <div className="flex items-center gap-2">
                  <span aria-hidden="true" className="text-lg text-[var(--color-lime)]">💾</span>
                  <span className="font-sans text-xs text-[var(--color-ink-hi)]">
                    {t("settings.exportBackupCallout")}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    handleCloseDelete();
                    void doExport();
                  }}
                  className="shrink-0 bg-[var(--color-lime)] px-3 py-1.5 font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-void)] hover:bg-[var(--color-lime-hi)] active:scale-95 transition-all"
                  style={{ borderRadius: "var(--radius-sharp)" }}
                >
                  {t("settings.exportFirstLink")}
                </button>
              </div>
            </div>

            <div className="shrink-0 border-t border-[var(--color-line)] pt-4">
              <label className="flex flex-col gap-2">
                <span className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink)]">
                  {t("settings.deleteConfirmLabel", { email: user?.email ?? "" })}
                </span>
                <input
                  type="email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  data-testid="delete-email-input"
                  className="border border-[var(--color-line-strong)] bg-[var(--color-void)] p-3 font-sans text-[var(--color-ink-hi)]"
                />
              </label>

              {offline && (
                <p className="mt-4 font-sans text-sm text-[var(--color-rust)]" data-testid="delete-offline">
                  {t("settings.deleteOffline")}
                </p>
              )}
              {deleteError && (
                <p className="mt-4 font-sans text-sm text-[var(--color-rust)]" data-testid="delete-error">
                  {deleteError}
                </p>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  ref={cancelBtnRef}
                  type="button"
                  onClick={handleCloseDelete}
                  disabled={busy}
                  data-testid="delete-cancel"
                  className="flex-1 border border-[var(--color-line-strong)] px-4 py-3 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink)] hover:bg-[var(--color-slab-2)] active:scale-95 transition-all"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="button"
                  onClick={doDelete}
                  disabled={busy || !emailMatch || offline}
                  data-testid="delete-confirm"
                  className="flex-1 bg-[var(--color-blood)] px-4 py-3 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink-hi)] hover:opacity-90 active:scale-95 disabled:opacity-50 transition-all"
                >
                  {t("settings.deleteConfirm")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
